import { Box } from '@mui/material';
import { TournamentStatus, TournamentSpectatorStatus } from '@okletsplay/core';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { TournamentEntity } from 'app/interfaces/entity';
import { sessionSelector } from 'app/store/session/selectors';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import { updateSpectatorStatus } from 'modules/tournaments/store/current-tournament/repository';
import { TournamentAlert } from 'shared/components/TournamentAlert';
import {
  currentTournamentSelector,
  currentTournamentSelectorFactory,
} from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { withTournamentStatus } from 'shared/HOC/withTournamentStatus';
import { usePlatformLinkAction } from 'shared/hooks';
import { useStyles as usePulseAnimationStyles } from 'shared/styles/PulseAnimation.styles';

const TournamentSpectatorJoinAlertComponent: FC = () => {
  const { classes: pulseAnimationClasses } = usePulseAnimationStyles();
  const dispatch = useDispatch();
  const activeUser = useSelector(sessionSelector.activeUser);
  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const spectator = useSelector(currentTournamentSelectorFactory.useSpectator(activeUser.id));
  const { openPlatformLink, isPlatformLinked } = usePlatformLinkAction(
    tournament?.game?.platformType
  );

  const updating = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.UpdatingSpectatorStatus,
  });

  const onClickHandler = useCallback(
    (status: TournamentSpectatorStatus) => {
      if (!isPlatformLinked && status === TournamentSpectatorStatus.Standby) {
        openPlatformLink();
        return;
      }

      if (!spectator) {
        return;
      }

      dispatch(
        updateSpectatorStatus({
          tournamentId: tournament.id,
          spectatorId: spectator.user.id,
          data: { status },
        })
      );
    },
    [dispatch, isPlatformLinked, openPlatformLink, spectator, tournament.id]
  );

  if (spectator?.status !== TournamentSpectatorStatus.Invited) {
    return null;
  }

  return (
    <Box mt={1}>
      <TournamentAlert
        title={`You were invited as a spectator to ${tournament.name} tournament!`}
        controls={
          <Box display="flex">
            <Box pr={1}>
              <LoaderButton
                variant="outlined"
                color="primary"
                loading={updating}
                onClick={() => onClickHandler(TournamentSpectatorStatus.Declined)}
              >
                Decline
              </LoaderButton>
            </Box>
            <LoaderButton
              color="primary"
              variant="contained"
              loading={updating}
              className={pulseAnimationClasses.pulse}
              onClick={() => onClickHandler(TournamentSpectatorStatus.Standby)}
            >
              Accept
            </LoaderButton>
          </Box>
        }
      />
    </Box>
  );
};

export const TournamentSpectatorJoinAlert = withTournamentStatus(
  TournamentSpectatorJoinAlertComponent,
  [TournamentStatus.Live, TournamentStatus.Pending, TournamentStatus.Checkin]
);
